import { Column, Columns } from "@r8er/components/marketing/columns";
import columnClasses from "@r8er/components/marketing/columns/columns.module.css";
import { Container } from "@r8er/components/marketing/container";
import { Content } from "@r8er/components/marketing/content";
import contentStyles from "@r8er/components/marketing/content/content.module.css";
import { Cta } from "@r8er/components/marketing/cta";
import { BookDemoButton } from "@r8er/components/marketing/ctas";
import { Divider } from "@r8er/components/marketing/divider";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { OurPartners } from "@r8er/components/marketing/our-members";
import { TeamBlockSimple } from "@r8er/components/marketing/team";
import { getAllR8ERs } from "@r8er/lib/cms";
import { withMiddleware } from "@r8er/lib/middleware";
import type { InferGetServerSidePropsType } from "next";
import Head from "next/head";

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: "translate(-25%,-40%)",
      opacity: "50%",
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

export const getServerSideProps = withMiddleware(
  async ({ req, prisma }) => {
    const r8ers = await getAllR8ERs();

    return {
      props: {
        r8ers,
      },
    };
  },
  { mode: "any" },
);

export default function About({
  r8ers,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <div>
      <Head>
        <title>About Us - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="About us" img="/about-hero.png" />

        <div style={{ position: "relative", overflow: "hidden" }}>
          <Watermark />

          <Container width={1100}>
            <Columns
              count={2}
              style={{
                padding: "var(--section-padding-normal)",
                paddingBottom: 0,
              }}
            >
              <Column>
                <h2
                  className="h2--standard"
                  style={{
                    fontSize: "calc(2.35 * var(--font-size-base))",
                    letterSpacing: "-1px",
                  }}
                >
                  We are the world's first, professionally vetted, digital
                  evaluation platform for disruptive innovation that connects
                  technology with investment.
                </h2>

                <Columns
                  count={2}
                  classList={[columnClasses.marketing__columns_matchmobile]}
                >
                  <Column>
                    <TeamBlockSimple
                      name="Dr. Milla Marinova"
                      title="Founder & CEO"
                      imageUrl="/about-milla.png"
                    />
                  </Column>
                  <Column>
                    <TeamBlockSimple
                      name="Mr. Pavel Marinov"
                      title="COO"
                      imageUrl="/about-pavel.png"
                    />
                  </Column>
                </Columns>
              </Column>
              <Column>
                <p className="p--standard">
                  The story of R8ER begins with Dr. Milla Marinova, who after
                  completing her medical and biotechnology degrees at
                  Universities in London and Cambridge realized that many of her
                  former peers were struggling to get their great ideas, either
                  to a format that would be clear for investors to easily
                  understand, or to get a foot in the door in terms of financing
                  their projects.
                </p>
                <p className="p--standard">
                  Together with her brother, Pavel Marinov who had already
                  gained experience in both the financial and IT sectors, they
                  formed an idea that would not only solve this problem, but
                  furthermore act as an accelerator for cutting-edge, disruptive
                  tech ideas - and so R8ER was born...
                </p>
                <p className="p--standard">
                  After sharing their idea with some of Britain's top medical
                  and financial experts, a shortlist of experts slowly emerged
                  who have now become key members of R8ER's Expert team.
                </p>
              </Column>
            </Columns>
          </Container>
        </div>
        <Container>
          <Divider />
        </Container>

        <Content backgroundImage="/about-start.png">
          <h2 className="h2--standard">Why R8ER?</h2>
          <p className="p--standard">
            Traditional fundraising can be time-consuming and inefficient.
          </p>
          <p className="p--standard">
            We combine technology with specialist expertise to create a more
            informed investment ecosystem.
          </p>
          <p className="p--standard">
            Every company receives an internal review from carefully selected
            experts before being introduced to investors whose preferences match
            the opportunity.
          </p>
          <p className="p--standard">
            The result is a more targeted fundraising process for entrepreneurs
            and more relevant deal flow for investors.
          </p>
        </Content>

        <OurPartners items={r8ers}>
          <h2 className="h2--standard">Our R8ER Expert Network</h2>
          <BookDemoButton text="Become a R8ER Expert" href="/auth/signup" />
          <Columns count={2}>
            <Column>
              <p className="p--standard">
                Our international expert community includes Investors,
                Professors, Consultant Clinicians, Scientists, Regulatory
                Specialists, Commercialisation Experts, Climate Specialists and
                Industry Leaders.
              </p>
            </Column>
            <Column>
              <p className="p--standard">
                Each company is reviewed by specialists chosen specifically for
                its technology, sector and stage. Here are some of our R8ERs:
              </p>
            </Column>
          </Columns>
        </OurPartners>

        <Content backgroundImage="/about-next.png">
          <h2 className="h2--standard">Beyond Investment</h2>
          <p className="p--standard">
            R8ER supports companies throughout their fundraising journey
            through:
          </p>
          <ul className={`ul--standard ${contentStyles.compactMarketingList}`}>
            <li>Expert feedback</li>
            <li>Investor introductions</li>
            <li>Pitching events</li>
            <li>Educational content</li>
            <li>Board and advisory connections</li>
            <li>Connect Partners</li>
            <li>Benefit Partners</li>
          </ul>
        </Content>

        <Cta
          title="Start your R8ER Journey Today"
          description="If you are looking for investment or looking to invest, sign up to R8ER today."
          buttonText="Get Started"
          buttonHref="/auth/signup"
          isBlue
        />

        <Newsletter />
      </Layout>
    </div>
  );
}
